import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { getHomepageBlocks, createHomepageBlock, updateHomepageBlock, deleteHomepageBlock, reorderHomepageBlocks } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import HomepageBlockForm from '../components/forms/HomepageBlockForm';
import { SortableBlockRow } from '../components/SortableBlockRow';

const GestioneHomepage = () => {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [blockToDelete, setBlockToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingBlock, setEditingBlock] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchBlocks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getHomepageBlocks();
            setBlocks(response.data);
        } catch (err) {
            console.error("Errore nel recupero dei blocchi della homepage:", err);
            setError("Impossibile caricare i blocchi della homepage.");
            toast.error("Errore nel caricamento dei blocchi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBlocks();
    }, [fetchBlocks]);

    const handleDeleteClick = (block) => {
        setBlockToDelete(block);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (blockToDelete) {
            try {
                await deleteHomepageBlock(blockToDelete.id);
                toast.success("Blocco eliminato con successo!");
                fetchBlocks();
            } catch (err) {
                console.error("Errore durante l'eliminazione del blocco:", err);
                toast.error("Errore durante l'eliminazione del blocco.");
            } finally {
                setShowDeleteModal(false);
                setBlockToDelete(null);
            }
        }
    };

    const handleShowNewModal = () => {
        setEditingBlock(null);
        setShowFormModal(true);
    };

    const handleShowEditModal = (block) => {
        setEditingBlock(block);
        setShowFormModal(true);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
        setEditingBlock(null);
    };

    const handleSave = async (formData, imageFile, mobileImageFile) => {
        const isNew = !formData.id;
        if (isNew) {
            await createHomepageBlock(formData, imageFile, mobileImageFile);
        } else {
            await updateHomepageBlock(formData.id, formData, imageFile, mobileImageFile);
        }
        fetchBlocks();
    };
    
    async function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = blocks.findIndex((b) => b.id === active.id);
            const newIndex = blocks.findIndex((b) => b.id === over.id);
            
            const newOrderBlocks = arrayMove(blocks, oldIndex, newIndex);
            setBlocks(newOrderBlocks); // Aggiorna UI immediatamente

            const orderedIds = newOrderBlocks.map(b => b.id);
            try {
                await reorderHomepageBlocks(orderedIds);
                toast.success("Ordine dei blocchi salvato!");
                // Ricarica i dati per essere sicuro che l'ordine nel DB sia corretto
                fetchBlocks();
            } catch (err) {
                toast.error("Errore durante il salvataggio dell'ordine.");
                // Ripristina l'ordine precedente in caso di errore
                setBlocks(blocks);
            }
        }
    }

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col xs={12} md>
                    <h1 className="h3 mb-2 text-gray-800">Gestione Layout Homepage</h1>
                    <p className="mb-4">
                        Trascina i blocchi usando l'icona <FontAwesomeIcon icon={faGripVertical} /> per riordinarli.
                    </p>
                </Col>
                <Col xs={12} md="auto" className="d-flex justify-content-end align-items-start">
                    <Button variant="primary" className="w-100" onClick={handleShowNewModal}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Nuovo Blocco
                    </Button>
                </Col>
            </Row>
            <Card>
                <Card.Body className="p-2 p-md-3">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={blocks.map(b => b.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th style={{width: '40px'}}></th>
                                        <th>Titolo Blocco</th>
                                        <th className="d-none d-md-table-cell">Tipo</th>
                                        <th className="d-none d-md-table-cell">Stato</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blocks.map(block => (
                                        <SortableBlockRow
                                            key={block.id}
                                            block={block}
                                            handleShowEditModal={handleShowEditModal}
                                            handleDeleteClick={handleDeleteClick}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                    {blocks.length === 0 && !loading && (
                        <div className="text-center p-3">Nessun blocco trovato. Inizia creandone uno nuovo.</div>
                    )}
                </Card.Body>
            </Card>

            <ConfirmModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleConfirm={handleConfirmDelete}
                title="Conferma Eliminazione"
                body={`Sei sicuro di voler eliminare il blocco "${blockToDelete?.title}"?`}
            />

            <HomepageBlockForm
                show={showFormModal}
                handleClose={handleCloseFormModal}
                block={editingBlock}
                onSave={handleSave}
            />
        </Container>
    );
};

export default GestioneHomepage;
